import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReportShell, { useReportDates } from '../../components/reports/ReportShell';
import {
  ChartCard,
  KpiCard,
  SimpleArea,
  SimpleLine,
} from '../../components/reports/ReportCharts';
import { reportsApi } from '../../services/reportsApi';
import { downloadCsv } from '../../components/reports/reportExport';

type Props = {
  open: boolean;
  memberId: number | null;
  memberName?: string;
  onClose: () => void;
};

const MemberReportDialog: React.FC<Props> = ({ open, memberId, memberName, onClose }) => {
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useReportDates(14);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<any>(null);
  const [bio, setBio] = useState<any>(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    const period = { date_from: dateFrom, date_to: dateTo };
    try {
      await reportsApi.ping();
      const [ov, b] = await Promise.all([
        reportsApi.memberOverview(memberId, period),
        reportsApi.memberBiometrics(memberId, period),
      ]);
      setOverview(ov);
      setBio(b);
    } catch (e: any) {
      setError(e?.message || '회원 리포트 조회 실패');
      setOverview(null);
      setBio(null);
    } finally {
      setLoading(false);
    }
  }, [memberId, dateFrom, dateTo]);

  useEffect(() => {
    if (open && memberId) void load();
  }, [open, memberId, load]);

  const onCsv = () => {
    if (!bio) return;
    const rows: (string | number)[][] = [['metric', 't', 'v']];
    (bio.heart_rate?.series || []).forEach((p: any) => rows.push(['hr', p.t, p.v]));
    (bio.spo2?.series || []).forEach((p: any) => rows.push(['spo2', p.t, p.v]));
    (bio.body_temperature?.series || []).forEach((p: any) => rows.push(['temp', p.t, p.v]));
    downloadCsv(`member_${memberId}_biometrics.csv`, rows);
  };

  const hrSeries = (bio?.heart_rate?.series || []).map((p: any) => ({
    t: (p.t || '').slice(5, 16),
    v: p.v,
  }));
  const spo2Series = (bio?.spo2?.series || []).map((p: any) => ({
    t: (p.t || '').slice(5, 16),
    v: p.v,
  }));
  const tempSeries = (bio?.body_temperature?.series || []).map((p: any) => ({
    t: (p.t || '').slice(5, 16),
    v: p.v,
  }));
  const motionSeries = (bio?.motion?.series || []).map((p: any) => ({
    t: (p.t || '').slice(5, 16),
    v: p.v,
  }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        회원 리포트{memberName ? ` — ${memberName}` : ''}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <ReportShell
          title={`회원 리포트${memberName ? ` — ${memberName}` : ''}`}
          printRootId={`member-report-print-${memberId || 0}`}
          loading={loading}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFrom={setDateFrom}
          onDateTo={setDateTo}
          onRefresh={load}
          onCsv={onCsv}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} className="no-print" variant="scrollable">
            <Tab label="요약" />
            <Tab label="심박" />
            <Tab label="SpO₂" />
            <Tab label="체온" />
            <Tab label="혈압·모션" />
            <Tab label="이상·이벤트" />
            <Tab label="세션" />
          </Tabs>

          {tab === 0 && overview && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <KpiCard label="세션" value={overview.sessions_count ?? 0} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="체류(분)" value={overview.session_minutes ?? 0} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="이벤트" value={overview.events_count ?? 0} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard label="이상 신호" value={bio?.anomaly_count ?? 0} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  {overview.member?.name} · {overview.member?.pool_name} · 혈액형{' '}
                  {overview.member?.blood_type} · 연락처 {overview.member?.phone}
                </Typography>
              </Grid>
            </Grid>
          )}

          {tab === 1 && bio && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <KpiCard label="평균 HR" value={bio.heart_rate?.stats?.avg ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최소" value={bio.heart_rate?.stats?.min ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최대" value={bio.heart_rate?.stats?.max ?? '-'} />
              </Grid>
              <Grid item xs={12}>
                <ChartCard title="심박 시계열" height={320}>
                  <SimpleArea data={hrSeries} xKey="t" yKey="v" color="#d32f2f" />
                </ChartCard>
              </Grid>
            </Grid>
          )}

          {tab === 2 && bio && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <KpiCard label="평균 SpO₂" value={bio.spo2?.stats?.avg ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최소" value={bio.spo2?.stats?.min ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최대" value={bio.spo2?.stats?.max ?? '-'} />
              </Grid>
              <Grid item xs={12}>
                <ChartCard title="산소포화도" height={320}>
                  <SimpleLine data={spo2Series} xKey="t" yKey="v" color="#1565c0" />
                </ChartCard>
              </Grid>
            </Grid>
          )}

          {tab === 3 && bio && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <KpiCard label="평균 체온" value={bio.body_temperature?.stats?.avg ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최소" value={bio.body_temperature?.stats?.min ?? '-'} />
              </Grid>
              <Grid item xs={4}>
                <KpiCard label="최대" value={bio.body_temperature?.stats?.max ?? '-'} />
              </Grid>
              <Grid item xs={12}>
                <ChartCard title="체온" height={320}>
                  <SimpleLine data={tempSeries} xKey="t" yKey="v" color="#ed6c02" />
                </ChartCard>
              </Grid>
            </Grid>
          )}

          {tab === 4 && bio && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  혈압 측정
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>시각</TableCell>
                      <TableCell>수축</TableCell>
                      <TableCell>이완</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bio.blood_pressure?.series || []).slice(-30).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.t}</TableCell>
                        <TableCell>{r.systolic ?? '-'}</TableCell>
                        <TableCell>{r.diastolic ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartCard title="모션 크기" height={280}>
                  <SimpleArea data={motionSeries} xKey="t" yKey="v" color="#00838f" />
                </ChartCard>
              </Grid>
            </Grid>
          )}

          {tab === 5 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  생체 이상 ({bio?.anomaly_count ?? 0})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>지표</TableCell>
                      <TableCell>시각</TableCell>
                      <TableCell>값</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bio?.anomalies || []).map((a: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{a.metric}</TableCell>
                        <TableCell>{a.t}</TableCell>
                        <TableCell>{a.v}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  관련 이벤트
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>시각</TableCell>
                      <TableCell>유형</TableCell>
                      <TableCell>위험</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(overview?.recent_events || []).map((e: any) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.created_at}</TableCell>
                        <TableCell>{e.event_type}</TableCell>
                        <TableCell>{e.risk_level}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          )}

          {tab === 6 && overview && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>시작</TableCell>
                  <TableCell>종료</TableCell>
                  <TableCell>장치</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(overview.sessions || []).map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.start_time}</TableCell>
                    <TableCell>{s.end_time || '(진행중)'}</TableCell>
                    <TableCell>{s.device_id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ReportShell>
      </DialogContent>
    </Dialog>
  );
};

export default MemberReportDialog;
